/** Turns a manifest entry's category-specific `meta` into display fields for the detail pane — pure, no DOM. */

import type {
  AssetCategory,
  AssetMeta,
  AudioMeta,
  FontMeta,
  ModelMeta,
  PaletteMeta,
  ScreenMeta,
  SpriteMeta,
  WorldMeta,
} from "@red-opus/baker/schema";

export interface MetaField {
  readonly label: string;
  readonly value: string;
}

function paletteField(match: { palette: string; method: string }): MetaField {
  return { label: "Palette", value: `${match.palette} (${match.method})` };
}

/**
 * `AudioDevice` is a 3-way union (`packages/baker/src/manifest-types.ts`): `"mt32"`/`"adlib"` name
 * a `.SNG` device track alone, rendered through a general-purpose synthesiser; `"adlib-custom-bank"`
 * — every baked `sfx` entry, post AdLib-SFX-lane rebake — means the AdLib track went through an
 * emulated OPL2 loaded with the game's own instrument bank instead, which is the only combination
 * that reproduces the original gunfire/engine timbres. A plain `=== "mt32" ? "MT-32" : "AdLib"`
 * ternary would have silently mislabelled that as generic "AdLib" (technically not false, but it
 * throws away the one fact — authentic vs. generic voicing — this pane exists to show).
 */
function audioDeviceLabel(device: AudioMeta["device"]): string {
  switch (device) {
    case "mt32":
      return "MT-32";
    case "adlib":
      return "AdLib";
    case "adlib-custom-bank":
      return "AdLib (authentic — game's own bank)";
    default: {
      const exhaustive: never = device;
      throw new Error(`audioDeviceLabel: unknown device '${String(exhaustive)}'`);
    }
  }
}

/**
 * A `.TBL` object is not only triangles: 55 of the 360 in the corpus draw
 * nothing but line segments (`TERR*` roads/rivers) or sprite billboards
 * (`FLAK`, `EXPLO`, `SMOKE`) — docs/formats/models-tbl.md §4.3. Showing only
 * a triangle count made every one of those read as an empty model, which is
 * how the bug this pane now documents went unnoticed, so the primitive
 * counts are always listed and a sprite/line-only object says so outright.
 */
function modelFields(m: ModelMeta): MetaField[] {
  const highestLod = m.lods[m.lods.length - 1];
  const total = (pick: (lod: ModelMeta["lods"][number]) => number): number => m.lods.reduce((sum, lod) => sum + pick(lod), 0);
  const primitives = [
    `${highestLod?.triangleCount ?? 0} tri`,
    `${highestLod?.lineCount ?? 0} lines`,
    `${highestLod?.billboardCount ?? 0} sprites`,
    `${highestLod?.sphereCount ?? 0} spheres`,
  ].join(", ");

  const fields: MetaField[] = [
    { label: "LODs", value: String(m.lods.length) },
    { label: "Highest LOD", value: primitives },
    { label: "Bounding radius", value: `${m.boundingRadius.toFixed(2)} m` },
    { label: "Colour mapping", value: `${m.colorMapping.palette} (${m.colorMapping.method}${m.colorMapping.inferred ? ", inferred" : ""})` },
  ];
  if (m.paint) {
    // A fighter ships colour-key markers, not colours; this is which of
    // RB.EXE's schemes the entry was repainted in (shading-and-paint.md §4).
    const residues = m.paint.markerResidues > 0 ? `, ${m.paint.markerResidues} unlisted marker face(s)` : "";
    fields.push({
      label: "Paint scheme",
      value: `${m.paint.scheme} — ${m.paint.colours.join("/")} on ${m.paint.paintedFaces} faces, ${m.paint.options.length} baked${residues}`,
    });
  }
  if (m.spriteSheet) {
    fields.push({ label: "Sprite sheet", value: `${m.spriteSheet.sheet} (${m.spriteSheet.frames.length} frames, ${total((lod) => lod.billboardCount)} refs)` });
  }
  if (m.spriteBased) {
    fields.push({ label: "Geometry", value: "sprite/line only — no triangles at any LOD" });
  }
  return fields;
}

export function summarizeMeta(category: AssetCategory, meta: AssetMeta): MetaField[] {
  switch (category) {
    case "palettes": {
      const m = meta as PaletteMeta;
      return [{ label: "Colors", value: String(m.entries) }];
    }
    case "screens": {
      const m = meta as ScreenMeta;
      return [{ label: "Dimensions", value: `${m.width}×${m.height}` }, paletteField(m.paletteMatch)];
    }
    case "sprites": {
      const m = meta as SpriteMeta;
      return [
        { label: "Frames", value: String(m.frameCount) },
        { label: "Sheet size", value: `${m.sheetWidth}×${m.sheetHeight}` },
        paletteField(m.paletteMatch),
      ];
    }
    case "fonts": {
      const m = meta as FontMeta;
      return [
        { label: "Glyphs", value: String(m.numSymbols) },
        { label: "Glyph size", value: `${m.fontWidth}×${m.fontHeight} (baseline ${m.baseLine})` },
      ];
    }
    case "models":
      return modelFields(meta as ModelMeta);
    case "music":
    case "sfx": {
      const m = meta as AudioMeta;
      const minutes = Math.floor(m.durationSeconds / 60);
      const seconds = m.durationSeconds - minutes * 60;
      return [
        { label: "Duration", value: `${minutes}:${seconds.toFixed(1).padStart(4, "0")}` },
        { label: "Device", value: audioDeviceLabel(m.device) },
        { label: "Channels", value: String(m.channels) },
        { label: "Notes", value: String(m.noteCount) },
      ];
    }
    case "world": {
      const m = meta as WorldMeta;
      return [
        { label: "Region", value: `${m.region}${m.regionPairingInferred ? " (inferred pairing)" : ""}` },
        { label: "Source", value: `${m.sourceWld} + ${m.sourceTbl}` },
        { label: "Placements", value: `${m.placementCount} (${m.resolvedCount} resolved, ${m.unresolvedCount} unresolved)` },
        { label: "Aerodromes", value: String(m.aerodromeCount) },
        { label: "Bounds", value: `x ${m.bounds.minX}…${m.bounds.maxX}, y ${m.bounds.minY}…${m.bounds.maxY}` },
      ];
    }
    default: {
      const exhaustive: never = category;
      throw new Error(`summarizeMeta: unknown category '${String(exhaustive)}'`);
    }
  }
}
